import { MouseEvent, PropsWithChildren, useState } from 'react'

import Close from '@mui/icons-material/Close'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import {
  alpha,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  lighten,
  Typography,
  useTheme
} from '@mui/material'

import type { ConsoleDialogProps, OnDialogCloseReason } from './ConsoleDialog.types'

export const CONSOLE_DIALOG_TEST_ID = {
  dialog: 'dialog-id',
  title: 'dialog-title-id',
  content: 'dialog-content-id',
  actions: 'dialog-actions-id',
  closeIconButton: 'dialog-close-icon-button-id',
  close: 'dialog-close-button-id',
  submit: 'dialog-submit-button-id',
  actionButton: 'dialog-action-button-id'
} as const

export function ConsoleDialog({
  open,
  onClose,
  SaveButtonProps,
  CloseButtonProps,
  ActionButtonProps,
  PaperProps,
  title,
  smallTopMargin = false,
  description,
  disableActions = false,
  backdropClickClose = false,
  escapeClickClose = true,
  dialogSize = 'sm',
  contentPadding = 2.5,
  fullHeight = false,
  fullWidth = true,
  children,
  width,
  horizontalPadding = 'default',
  dataTestId,
  customActionButton,
  hideCloseAction = false,
  contentSx
}: PropsWithChildren<ConsoleDialogProps>) {
  const { palette, typography } = useTheme()
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const { onClick, disabled, loading, sx, text, error } = SaveButtonProps || {}

  const onDialogClose = (_: MouseEvent, reason: OnDialogCloseReason) => {
    if (reason === 'backdropClick' && backdropClickClose === false) {
      return
    }

    if (reason === 'escapeKeyDown' && escapeClickClose === false) {
      return
    }

    onClose?.()
  }

  const onSave = async () => {
    try {
      setIsLoading(true)
      await onClick?.()
      setIsLoading(false)
      onClose?.()
    } catch (e) {
      setIsLoading(false)
    }
  }

  const onCancelClick = (event: MouseEvent<HTMLButtonElement>) => {
    CloseButtonProps?.onClick?.(event)
    onClose?.()
  }

  return (
    <Dialog
      fullWidth={fullWidth}
      open={!!open}
      onContextMenu={(e) => e.stopPropagation()}
      onClose={onDialogClose}
      maxWidth={dialogSize}
      slotProps={{
        paper: {
          ...PaperProps,
          sx: {
            position: 'fixed',
            width,
            top: smallTopMargin ? 16 : 64,
            maxHeight: `calc(100% - ${smallTopMargin ? 82 : 128}px)`,
            height: fullHeight ? `calc(100% - ${smallTopMargin ? 82 : 128}px)` : 'auto',
            ...PaperProps?.sx
          }
        }
      }}
      data-testid={dataTestId || CONSOLE_DIALOG_TEST_ID.dialog}
      onClick={(e) => e.stopPropagation()}
    >
      <DialogTitle data-testid={CONSOLE_DIALOG_TEST_ID.title}>
        <Grid container direction="row" alignItems="center" columnSpacing={0.25} wrap="nowrap">
          <Grid container direction="column" flexGrow={1} rowSpacing={0.75}>
            <Grid size={{ xs: 12 }}>
              <Typography variant="h5" sx={{ flexGrow: 1, fontWeight: typography.fontWeightBold }}>
                {title}
              </Typography>
            </Grid>

            {description && (
              <Grid>
                <Typography variant="body2" sx={{ color: palette.text.secondary }}>
                  {description}
                </Typography>
              </Grid>
            )}
          </Grid>

          {!hideCloseAction && (
            <Grid size={{ xs: 'auto' }}>
              <IconButton
                onClick={onCancelClick}
                data-testid={CONSOLE_DIALOG_TEST_ID.closeIconButton}
              >
                <Close />
              </IconButton>
            </Grid>
          )}
        </Grid>
      </DialogTitle>

      <DialogContent
        onClick={(e) => e.stopPropagation()}
        sx={{ p: horizontalPadding }}
        data-testid={CONSOLE_DIALOG_TEST_ID.content}
      >
        <Box height={fullHeight ? '100%' : 'auto'} pt={contentPadding} sx={contentSx}>
          {children}
        </Box>
      </DialogContent>

      {!disableActions && (
        <DialogActions
          sx={{
            minHeight: 54,
            px: 3,
            pb: 1.25,
            backgroundColor:
              palette.mode === 'light'
                ? palette.background.paper
                : lighten(palette.background.paper, 0.05)
          }}
          data-testid={CONSOLE_DIALOG_TEST_ID.actions}
        >
          {customActionButton}

          {ActionButtonProps !== undefined && (
            <Button
              variant="text"
              color="secondary"
              size="small"
              sx={{
                color: palette.text.secondary,
                mr: 'auto',
                '&:hover': {
                  backgroundColor: alpha(palette.text.secondary, 0.05)
                },
                ...ActionButtonProps?.sx
              }}
              data-testid={CONSOLE_DIALOG_TEST_ID.actionButton}
              {...ActionButtonProps}
            >
              {ActionButtonProps.text}
            </Button>
          )}

          {SaveButtonProps !== undefined && (
            <Button
              size="small"
              color="primary"
              variant="contained"
              disabled={disabled}
              loading={loading || isLoading}
              sx={sx}
              onClick={onSave}
              data-testid={CONSOLE_DIALOG_TEST_ID.submit}
            >
              {!!error && <ErrorOutlineIcon color="error" fontSize="small" sx={{ mr: 1 }} />}
              {text || 'Save'}
            </Button>
          )}

          {!hideCloseAction && (
            <Button
              size="small"
              variant="outlined"
              color="primary"
              {...CloseButtonProps}
              onClick={onCancelClick}
              data-testid={CONSOLE_DIALOG_TEST_ID.close}
            >
              {CloseButtonProps?.text || 'Close'}
            </Button>
          )}
        </DialogActions>
      )}
    </Dialog>
  )
}
